import { Link } from "react-router-dom";
import { EnvelopeSimple, MapPin, FacebookLogo, XLogo, LinkedinLogo } from "@phosphor-icons/react";
import { useLanguage } from "@/contexts/LanguageContext";

const LOGO_URL = "/kybia-logo.png";

const SOCIAL_LINKS = [
  { Icon: FacebookLogo, href: "https://www.facebook.com/profile.php?id=61590691922004", label: "Facebook" },
  { Icon: XLogo, href: "https://x.com/KYBIAOrg", label: "X" },
  { Icon: LinkedinLogo, href: "https://www.linkedin.com/company/kybia", label: "LinkedIn" },
];

export default function Footer() {
  const { t } = useLanguage();
  const footerLinks = [
    { label: t("nav_home"), path: "/" },
    { label: t("nav_about"), path: "/about" },
    { label: t("nav_research"), path: "/research" },
    { label: t("nav_contact"), path: "/contact" },
  ];
  return (
    <footer
      data-testid="footer"
      style={{ backgroundColor: '#1A5C37', color: '#F7F5F0' }}
      className="py-16 lg:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={LOGO_URL}
                alt="KYBIA Logo"
                className="h-12 w-12 object-contain"
              />
              <span className="font-heading text-2xl font-semibold tracking-tight text-white">
                KYBIA
              </span>
            </div>
            <p
              className="text-sm leading-relaxed max-w-sm"
              style={{ color: 'rgba(247, 245, 240, 0.7)' }}
            >
              {t("footer_desc")}
            </p>
            <div className="flex items-center gap-3 mt-5">
              {SOCIAL_LINKS.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: 'rgba(247, 245, 240, 0.1)', color: '#F7F5F0' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(247, 245, 240, 0.22)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(247, 245, 240, 0.1)')}
                >
                  <Icon size={18} weight="fill" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="md:col-span-3">
            <h4 className="font-heading text-lg font-medium text-white mb-4">{t("footer_quick_links")}</h4>
            <nav className="flex flex-col gap-2.5">
              {footerLinks.map((link, i) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`footer-link-${i}`}
                  className="text-sm transition-colors duration-200"
                  style={{ color: 'rgba(247, 245, 240, 0.7)' }}
                  onMouseEnter={(e) => e.target.style.color = '#F7F5F0'}
                  onMouseLeave={(e) => e.target.style.color = 'rgba(247, 245, 240, 0.7)'}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="font-heading text-lg font-medium text-white mb-4">{t("footer_contact_label")}</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-2.5">
                <MapPin size={18} weight="fill" style={{ color: 'rgba(247, 245, 240, 0.7)', marginTop: 2, flexShrink: 0 }} />
                <span className="text-sm" style={{ color: 'rgba(247, 245, 240, 0.7)' }}>
                  {t("footer_location")}
                </span>
              </div>
              <div className="flex items-start gap-2.5">
                <EnvelopeSimple size={18} weight="fill" style={{ color: 'rgba(247, 245, 240, 0.7)', marginTop: 2, flexShrink: 0 }} />
                <span className="text-sm" style={{ color: 'rgba(247, 245, 240, 0.7)' }}>
                  info@kybia.org
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="mt-12 pt-8 border-t text-center"
          style={{ borderColor: 'rgba(247, 245, 240, 0.15)' }}
        >
          <p className="text-xs" style={{ color: 'rgba(247, 245, 240, 0.5)' }}>
            &copy; {new Date().getFullYear()} KYBIA — {t("footer_org_full")}. {t("footer_rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}
